import { create } from "zustand";
import { persist } from "zustand/middleware";
import { cloneBaseline } from "./site-data";
import type { Item, Section, Selection, SiteConfig } from "./types";

interface EditorState {
  site: SiteConfig;
  past: SiteConfig[];
  future: SiteConfig[];
  editing: boolean;
  preview: boolean;
  selection: Selection | null;
  onboardingSeen: boolean;
  saveState: "local" | "saving" | "saved" | "offline";
  toast: string | null;
  setEditing: (value: boolean) => void;
  setPreview: (value: boolean) => void;
  select: (selection: Selection | null) => void;
  updateSite: (
    recipe: (site: SiteConfig) => void,
    checkpoint?: boolean,
  ) => void;
  updateSection: (
    id: string,
    recipe: (section: Section) => void,
    checkpoint?: boolean,
  ) => void;
  updateItem: (
    sectionId: string,
    itemId: string,
    recipe: (item: Item) => void,
    checkpoint?: boolean,
  ) => void;
  reorderSections: (activeId: string, overId: string) => void;
  reorderItems: (sectionId: string, activeId: string, overId: string) => void;
  duplicateSection: (id: string) => void;
  deleteSection: (id: string) => void;
  addSection: (section: Section) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  dismissOnboarding: () => void;
  setSaveState: (value: EditorState["saveState"]) => void;
  showToast: (message: string | null) => void;
  hydrateRemote: (site: SiteConfig) => void;
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
let lastCheckpoint = 0;

export const useEditor = create<EditorState>()(
  persist(
    (set, get) => ({
      site: cloneBaseline(),
      past: [],
      future: [],
      editing: false,
      preview: false,
      selection: null,
      onboardingSeen: false,
      saveState: "local",
      toast: null,
      setEditing: (editing) =>
        set({ editing, preview: false, selection: null }),
      setPreview: (preview) => set({ preview, selection: null }),
      select: (selection) => set({ selection }),
      updateSite: (recipe, checkpoint = true) =>
        set((state) => {
          const next = clone(state.site);
          recipe(next);
          const now = Date.now();
          const shouldCheckpoint = checkpoint && now - lastCheckpoint > 450;
          if (shouldCheckpoint) lastCheckpoint = now;
          return {
            site: next,
            past: shouldCheckpoint
              ? [...state.past.slice(-49), state.site]
              : state.past,
            future: shouldCheckpoint ? [] : state.future,
            saveState: "local",
          };
        }),
      updateSection: (id, recipe, checkpoint = true) =>
        get().updateSite((site) => {
          const section = site.sections.find((item) => item.id === id);
          if (section) recipe(section);
        }, checkpoint),
      updateItem: (sectionId, itemId, recipe, checkpoint = true) =>
        get().updateSection(
          sectionId,
          (section) => {
            const items = section.content.items as Item[] | undefined;
            const item = items?.find((entry) => entry.id === itemId);
            if (item) recipe(item);
          },
          checkpoint,
        ),
      reorderSections: (activeId, overId) =>
        get().updateSite((site) => {
          const from = site.sections.findIndex((s) => s.id === activeId),
            to = site.sections.findIndex((s) => s.id === overId);
          if (from < 0 || to < 0) return;
          const [moved] = site.sections.splice(from, 1);
          site.sections.splice(to, 0, moved);
        }),
      reorderItems: (sectionId, activeId, overId) =>
        get().updateSection(sectionId, (section) => {
          const items = section.content.items as Item[];
          const from = items.findIndex((i) => i.id === activeId),
            to = items.findIndex((i) => i.id === overId);
          if (from < 0 || to < 0) return;
          const [moved] = items.splice(from, 1);
          items.splice(to, 0, moved);
        }),
      duplicateSection: (id) =>
        get().updateSite((site) => {
          const index = site.sections.findIndex((s) => s.id === id);
          if (index < 0) return;
          const copy = clone(site.sections[index]);
          copy.id = `${copy.type}-${crypto.randomUUID()}`;
          site.sections.splice(index + 1, 0, copy);
        }),
      deleteSection: (id) =>
        get().updateSite((site) => {
          if (site.sections.length > 3)
            site.sections = site.sections.filter((s) => s.id !== id);
        }),
      addSection: (section) =>
        get().updateSite((site) => {
          site.sections.push(section);
        }),
      undo: () =>
        set((state) =>
          state.past.length
            ? {
                site: state.past.at(-1)!,
                past: state.past.slice(0, -1),
                future: [state.site, ...state.future],
                selection: null,
              }
            : {},
        ),
      redo: () =>
        set((state) =>
          state.future.length
            ? {
                site: state.future[0],
                future: state.future.slice(1),
                past: [...state.past, state.site],
                selection: null,
              }
            : {},
        ),
      reset: () =>
        set((state) => ({
          site: cloneBaseline(),
          past: [...state.past.slice(-49), state.site],
          future: [],
          selection: null,
          saveState: "local",
          toast: "Demo restored to the original design",
        })),
      dismissOnboarding: () => set({ onboardingSeen: true }),
      setSaveState: (saveState) => set({ saveState }),
      showToast: (toast) => set({ toast }),
      hydrateRemote: (site) =>
        set((state) => (state.past.length ? {} : { site })),
    }),
    {
      name: "zhstudio-cms-demo-v2",
      partialize: (state) => ({
        site: state.site,
        onboardingSeen: state.onboardingSeen,
      }),
    },
  ),
);
